import React, { useContext, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import initAddMessageSaga from 'components/AddMessage/saga';
import HelpPlacePin from 'components/HelpPlacePin';
import pin from 'components/PlanViewer/pin.svg';
import { ProjectContext } from 'containers/GlobalWrapper/ProjectDataValidation';
import { selectModules } from 'containers/GlobalWrapper/selectors';
import AddForm from 'containers/OneProjectPage/FormsPage/components/AddForm';
import { getGroups } from 'containers/OneProjectPage/GroupsPage/actions';
import groupsPageReducer from 'containers/OneProjectPage/GroupsPage/reducer';
import initGroupsPageSaga from 'containers/OneProjectPage/GroupsPage/saga';
import { selectGroups } from 'containers/OneProjectPage/GroupsPage/selectors';
import AddObservation from 'containers/OneProjectPage/ObservationsPage/components/AddObservation';
import observationsPageReducer from 'containers/OneProjectPage/ObservationsPage/reducer';
import initObservationsPageSaga from 'containers/OneProjectPage/ObservationsPage/saga';
import useCurrentUser from 'hooks/useCurrentUser';
import { MODULE_TYPES, PROJECT_USER_ROLE, authorizations } from 'utils/constants';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';

export const AddItemButton = ({
  match,
  entityType,
  dispatchGetGroups,
  groups,
  moduleId,
  updatePin,
  isPinsVisible,
  showCustomPin,
  hideCustomPin,
  triggerClickToPlacePin,
  isViewerFull,
  onClickFromItem,
  isAnnotating,
}) => {
  const currentUser = useCurrentUser();
  const [isClickToPlaceMode, setIsClickToPlaceMode] = useState(!!onClickFromItem);
  const componentRef = useRef(null);
  const [isCreateItemDialogOpen, setIsCreateItemDialogOpen] = useState(false);
  const [position, setPosition] = useState();
  const modules = useSelector(selectModules());
  const [KeyDownListener, setKeyDownListener] = useState();

  const project = useContext(ProjectContext);

  const [canCreateObservation, setCanCreateObservation] = useState(
    project?.authorizations?.indexOf(authorizations.project.createObservation) !== -1,
  );

  useEffect(
    () => {
      if (
        isClickToPlaceMode &&
        Number.isFinite(triggerClickToPlacePin?.x) &&
        Number.isFinite(triggerClickToPlacePin?.y)
      ) {
        setPosition({
          x: triggerClickToPlacePin?.x,
          y: triggerClickToPlacePin?.y,
          planId: match.params.planId,
        });
        setIsCreateItemDialogOpen(true);
        setIsClickToPlaceMode(false);
        isPinsVisible(false);
      }
    },
    [triggerClickToPlacePin],
  );

  useEffect(
    () => {
      if (!project?.authorizations?.includes(authorizations.project.createObservation)) {
        dispatchGetGroups(match.params);
      }
    },
    [dispatchGetGroups, match.params, project],
  );

  useEffect(
    () => {
      if (!project?.authorizations?.includes(authorizations.project.createObservation)) {
        setCanCreateObservation(
          groups.some(group =>
            group?.authorizations?.includes(authorizations.project.createObservation),
          ),
        );
      }
    },
    [groups, project],
  );

  const handleHideCustomPin = () => {
    setIsCreateItemDialogOpen(false);
    setIsClickToPlaceMode(false);
    isPinsVisible(false);
    if (hideCustomPin) {
      hideCustomPin();
    }
  };

  useEffect(
    () => {
      if (isClickToPlaceMode) {
        if (!KeyDownListener) {
          window.addEventListener('keydown', e => {
            if (e.key === 'Escape') {
              handleHideCustomPin();
            }
          });
        }
        setKeyDownListener(true);
      }
    },
    [isClickToPlaceMode, isCreateItemDialogOpen],
  );

  const handleCreateItem = () => {
    if (!isClickToPlaceMode) {
      setIsClickToPlaceMode(true);
      if (showCustomPin) {
        showCustomPin();
      } else {
        isPinsVisible(true);
      }

      return true;
    }
    setIsClickToPlaceMode(false);
    isPinsVisible(false);
    return false;
  };

  const getPosition = e => ({
    x: e.nativeEvent.layerX / componentRef.current?.getBoundingClientRect().width,
    y: e.nativeEvent.layerY / componentRef.current?.getBoundingClientRect().height,
    planId: match.params.planId,
  });

  const handleOnclickToAddItem = e => {
    if (isClickToPlaceMode) {
      if (onClickFromItem) {
        onClickFromItem(getPosition(e));
      } else {
        setPosition(getPosition(e));
        setIsCreateItemDialogOpen(true);
        setIsClickToPlaceMode(false);
        isPinsVisible(false);
      }
    } else {
      setIsCreateItemDialogOpen(false);
    }
  };

  const handleItemCreated = () => {
    updatePin();
    setIsCreateItemDialogOpen(false);
    setIsClickToPlaceMode(false);
  };

  if (!currentUser.projectRole) return null;
  const isGuest = currentUser.projectRole === PROJECT_USER_ROLE.guest;
  return (
    <>
      {!isViewerFull && (
        <div
          onClick={handleOnclickToAddItem}
          ref={componentRef}
          aria-hidden="true"
          style={{
            width: '100%',
            height: '100%',
            zIndex: 999,
            top: '0px',
            ...(isClickToPlaceMode ? { position: 'absolute' } : {}),
            ...(isClickToPlaceMode && !showCustomPin
              ? { cursor: `url("${pin}") 42 110, crosshair` }
              : {}),
          }}
        />
      )}
      {isClickToPlaceMode && !onClickFromItem && <HelpPlacePin />}
      <div
        style={{ position: 'fixed', bottom: '0px', transform: 'translate(-50%, 0%)', zIndex: 999 }}
      >
        {entityType === MODULE_TYPES.observations && (
          <AddObservation
            canWriteLibrary={
              project?.authorizations?.indexOf(authorizations.project.writeLibrary) !== -1
            }
            canActivateLibrary={
              project?.authorizations?.indexOf(authorizations.project.writeSettings) !== -1
            }
            canCreateObservation={canCreateObservation}
            disabled={isClickToPlaceMode || isAnnotating || isGuest}
            isGuest={isGuest}
            moduleId={moduleId}
            beforeOpenDialog={handleCreateItem}
            openDialog={isCreateItemDialogOpen}
            position={position}
            addButtonLabel={modules?.find(module => module?.id === moduleId)?.name}
            onObservationCreated={() => handleItemCreated()}
            disableRedirect
            onCloseDialog={handleHideCustomPin}
          />
        )}
        {entityType === MODULE_TYPES.forms && (
          <AddForm
            canCreateForm={project?.authorizations?.indexOf(authorizations.project.createForm) > -1}
            disabled={isClickToPlaceMode || isAnnotating || isGuest}
            isGuest={isGuest}
            moduleId={moduleId}
            beforeOpenDialog={handleCreateItem}
            openDialog={isCreateItemDialogOpen}
            position={position}
            addButtonLabel={modules?.find(module => module?.id === moduleId)?.name}
            disableRedirect
            onCloseDialog={handleHideCustomPin}
            onFormCreated={() => handleItemCreated()}
          />
        )}
      </div>
    </>
  );
};

AddItemButton.propTypes = {
  match: PropTypes.object,
  entityType: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.object),
  dispatchGetGroups: PropTypes.func.isRequired,
  moduleId: PropTypes.string,
  updatePin: PropTypes.func,
  isPinsVisible: PropTypes.func,
  showCustomPin: PropTypes.func,
  hideCustomPin: PropTypes.func,
  triggerClickToPlacePin: PropTypes.object,
  isViewerFull: PropTypes.bool,
  onClickFromItem: PropTypes.func,
  isAnnotating: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  groups: selectGroups(),
});

const mapDispatchToProps = {
  dispatchGetGroups: getGroups,
};

const withSaga = injectSaga({ key: 'observationsPage', saga: initObservationsPageSaga });
const withReducer = injectReducer({ key: 'observationsPage', reducer: observationsPageReducer });
const withSagaGroups = injectSaga({ key: 'groupsSaga', saga: initGroupsPageSaga });
const withGroupsReducer = injectReducer({ key: 'groupsPage', reducer: groupsPageReducer });
const withMessageSaga = injectSaga({ key: 'addMessageSaga', saga: initAddMessageSaga });

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withRouter,
  withConnect,
  withSaga,
  withReducer,
  withSagaGroups,
  withGroupsReducer,
  withMessageSaga,
)(AddItemButton);
